import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  Clip,
  CoachBriefV2,
  UnauthPreview,
  UnauthPreviewServer,
} from "@arc/brief-schema";
import { normalizeUnauthPreview } from "@arc/brief-schema";

export interface Collaboration {
  id: string;
  title: string;
  summary: string;
  ownerEmail: string | null;
  ownerPhone?: string | null;
  acceptedUserId: string | null;
  role: "coach" | "viewer";
  status: "pending" | "accepted" | "revoked";
  sessionId: string | null;
  shareCode: string;
  previewToken: string | null;
  briefV2: CoachBriefV2 | null;
  latestRangePlanStatus: CoachRangePlanPortalStatus | null;
  updatedAt: string;
  practiceHistory?: CoachPracticeSession[];
}

export interface CoachPracticeSession {
  id: string;
  practicedAt: string;
  club: string;
  label: string;
  outcome: string;
}

export type CoachAuthorKind = "player" | "coach" | "arc_ai";

export interface CoachAnnotation {
  id: string;
  collaborationId: string;
  authorUserId: string;
  authorKind: CoachAuthorKind;
  sessionId: string | null;
  swingId: string | null;
  title: string;
  body: string;
  tag: "note" | "cue" | "fix" | "observation" | "mobility" | "voice";
  scope: "session" | "swing" | "playbook";
  audioPath: string | null;
  audioDurationSeconds: number | null;
  createdAt: string;
  updatedAt: string;
}

export type CoachAssignmentStatus =
  | "draft"
  | "sent"
  | "acknowledged"
  | "completed"
  | "archived";

export type CoachRangePlanKind =
  | "coach_plan"
  | "homework"
  | "measurement_test"
  | "free_practice_guardrails";

export type CoachRangePlanPortalStatusState = "active" | "paused" | "completed";

export type CoachRangePlanPortalStatusBlockState =
  | "completed"
  | "active"
  | "pending";

export interface CoachRangePlanPortalStatusBlock {
  id: string;
  title: string;
  club: string;
  camera: string;
  completedBalls: number;
  totalBalls: number;
  state: CoachRangePlanPortalStatusBlockState;
}

export interface CoachRangePlanPortalStatus {
  schemaVersion: number;
  assignmentId: string;
  collaborationId: string;
  sessionId: string | null;
  planId: string;
  planTitle: string;
  focusLabel: string;
  clubLabel: string;
  state: CoachRangePlanPortalStatusState;
  totalBalls: number;
  completedBalls: number;
  currentBlockId: string | null;
  currentBlockTitle: string | null;
  nextBlockTitle: string | null;
  updatedAt: string;
  completedAt: string | null;
  blocks: CoachRangePlanPortalStatusBlock[];
}

export type CoachRangePlanBlockIntent =
  | "stock"
  | "mishit_mix"
  | "rehearsal"
  | "warmup"
  | "pressure";

export const COACH_RANGE_PLAN_BLOCK_INTENTS: readonly CoachRangePlanBlockIntent[] = [
  "stock",
  "mishit_mix",
  "rehearsal",
  "warmup",
  "pressure",
] as const;

export type CoachRangePlanBlock = {
  id: string;
  balls: number;
  title: string;
  club: string;
  camera: string;
  cadence: string;
  how: string;
  goal: string;
  intent?: CoachRangePlanBlockIntent;
  successCriteria?: string | null;
  checkpoint?: string | null;
};

export type CoachRangePlanGroundTruthMethod =
  | "voice_marks"
  | "launch_monitor"
  | "toptracer"
  | "manual"
  | "none";

export const COACH_RANGE_PLAN_GROUND_TRUTH_METHODS: readonly CoachRangePlanGroundTruthMethod[] = [
  "none",
  "voice_marks",
  "launch_monitor",
  "toptracer",
  "manual",
] as const;

export type CoachRangePlanGroundTruth = {
  method: CoachRangePlanGroundTruthMethod;
  cadence?: string | null;
};

export type CoachRangePlanPrescription = {
  id: string;
  templateId: string;
  title: string;
  kind: CoachRangePlanKind;
  totalBalls: number;
  summary: string;
  setup: string;
  coachCue: string;
  homework: string;
  betweenBlocks: string;
  focusLabel: string;
  clubLabel: string;
  blocks: CoachRangePlanBlock[];
  prerequisites?: string[];
  groundTruth?: CoachRangePlanGroundTruth | null;
};

export interface CoachAssignment {
  id: string;
  collaborationId: string;
  authorUserId: string;
  authorKind: CoachAuthorKind;
  sessionId: string | null;
  title: string;
  detail: string;
  dueDate: string | null;
  status: CoachAssignmentStatus;
  isCue: boolean;
  sourceDrillTemplateId: string | null;
  rangePlanPrescription: CoachRangePlanPrescription | null;
  createdAt: string;
  updatedAt: string;
}

export type ArcCoachActionDraftMode = "note" | "plan" | "cue";

export interface ArcCoachActionDraft {
  mode: ArcCoachActionDraftMode;
  title: string;
  body: string;
  suggestedRangePlanId: string | null;
  rangePlanPrescription: CoachRangePlanPrescription | null;
}

export interface ArcCoachPlanResult {
  ok: boolean;
  status: "planned" | "already_planned" | "already_running" | "skipped";
  run_id?: string;
  note_id?: string | null;
  result_check_note_id?: string | null;
  assignment_id?: string | null;
  cue_id?: string | null;
  plan?: CoachRangePlanPrescription;
  result_check?: {
    status: "better" | "same_pattern" | "regressed" | "not_enough_data";
    decision: "graduate" | "repeat" | "simplify" | "change_focus";
    summary: string;
  } | null;
  result?: Record<string, unknown>;
  error?: string;
}

export const COACH_DRILL_TEMPLATE_FOCUS_TAGS = [
  "general",
  "start_line",
  "tempo",
  "posture",
  "contact",
  "short_game",
  "putting",
  "mobility",
  "mental",
] as const;

export type CoachDrillTemplateFocusTag =
  (typeof COACH_DRILL_TEMPLATE_FOCUS_TAGS)[number];

export interface CoachDrillTemplate {
  id: string;
  ownerUserId: string;
  title: string;
  detail: string;
  focusTag: CoachDrillTemplateFocusTag;
  defaultDurationSwings: number | null;
  defaultDurationMinutes: number | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CoachDrillTemplateDraft {
  title: string;
  detail: string;
  focusTag: CoachDrillTemplateFocusTag;
  defaultDurationSwings: number | null;
  defaultDurationMinutes: number | null;
}

export async function listAcceptedCoachCollaborations(
  client: SupabaseClient,
): Promise<Collaboration[]> {
  const { data, error } = await client
    .from("coach_collaborations")
    .select(
      "id,title,summary,owner_email,accepted_user_id,role,status,session_id,share_code,preview_token,brief_v2,latest_range_plan_status,updated_at",
    )
    .eq("status", "accepted")
    .eq("role", "coach")
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(mapCollaboration);
}

export async function acceptCoachCollaboration(
  client: SupabaseClient,
  inviteCode: string,
): Promise<Collaboration> {
  const normalizedCode = inviteCode.trim().toUpperCase().replace(/\s+/g, "");
  if (!normalizedCode) throw new Error("Invite code is required.");

  const { data, error } = await client.rpc("accept_coach_collaboration", {
    share_code: normalizedCode,
  });
  if (error) {
    throw normalizeSupabaseClientError(error, "Could not join that workspace.");
  }
  if (!data) throw new Error("That invite did not return a coach workspace.");
  return mapCollaboration(data as Record<string, unknown>);
}

export async function getCollaboration(
  client: SupabaseClient,
  id: string,
): Promise<Collaboration | null> {
  const { data, error } = await client
    .from("coach_collaborations")
    .select(
      "id,title,summary,owner_email,accepted_user_id,role,status,session_id,share_code,preview_token,brief_v2,latest_range_plan_status,updated_at",
    )
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return signCollaborationRepresentativeSampleClips(client, mapCollaboration(data));
}

export function canUseSessionMedia(collaboration: Collaboration): boolean {
  const sessionId = collaboration.sessionId?.trim();
  return Boolean(sessionId);
}

type CoachMediaSignResponse = {
  ok?: boolean;
  signed_url?: string | null;
  expires_at?: number | string | null;
  error?: string | null;
};

export async function signCollaborationRepresentativeSampleClips(
  client: SupabaseClient,
  collaboration: Collaboration,
): Promise<Collaboration> {
  const brief = collaboration.briefV2;
  const shareCode = collaboration.shareCode.trim().toUpperCase();
  if (!brief || !shareCode || !canUseSessionMedia(collaboration)) {
    return collaboration;
  }

  const signedByObjectId = new Map<string, Promise<Clip>>();
  const signClip = (clip: Clip): Promise<Clip> => {
    const objectId = clipMediaObjectId(clip);
    if (!objectId) return Promise.resolve(clip);
    if (!clipNeedsSigning(clip)) return Promise.resolve(clip);
    const existing = signedByObjectId.get(objectId);
    if (existing) return existing;
    const promise = signCoachMediaClip(client, shareCode, objectId, clip);
    signedByObjectId.set(objectId, promise);
    return promise;
  };

  const representativeSamples = await Promise.all(
    brief.representativeSamples.map(async (sample) => ({
      ...sample,
      clip: await signClip(sample.clip),
    })),
  );
  const clipReferences = await Promise.all(
    brief.appendix.clipReferences.map(signClip),
  );

  return {
    ...collaboration,
    briefV2: {
      ...brief,
      representativeSamples,
      appendix: {
        ...brief.appendix,
        clipReferences,
      },
    },
  };
}

function clipMediaObjectId(clip: Clip): string {
  return (clip.clipId?.trim() || clip.swingId.trim()).replace(/\.mov$/i, "");
}

function clipNeedsSigning(clip: Clip): boolean {
  if (!clip.signedURL) return true;
  if (!clip.signedURLExpiresAt) return false;
  return Date.parse(clip.signedURLExpiresAt) <= Date.now() + 60_000;
}

async function signCoachMediaClip(
  client: SupabaseClient,
  shareCode: string,
  objectId: string,
  clip: Clip,
): Promise<Clip> {
  try {
    const { data, error } = await client.functions.invoke<CoachMediaSignResponse>(
      "session-media-r2-sign",
      {
        body: {
          action: "download",
          share_code: shareCode,
          swing_id: objectId,
        },
      },
    );
    if (error || !data?.signed_url) return clip;
    return {
      ...clip,
      signedURL: data.signed_url,
      signedURLExpiresAt: signExpiryISOString(data.expires_at),
    };
  } catch {
    return clip;
  }
}

function signExpiryISOString(expiresAt: number | string | null | undefined): string | null {
  const seconds = parseNullableNumber(expiresAt);
  if (seconds === null) return null;
  return new Date(seconds * 1000).toISOString();
}

export function collaborationSurfaceTitle(collaboration: Collaboration): string {
  return canUseSessionMedia(collaboration) ? "Student workspace" : "Student coaching";
}

export async function listAnnotations(
  client: SupabaseClient,
  collaborationId: string,
): Promise<CoachAnnotation[]> {
  const { data, error } = await client
    .from("coach_annotations")
    .select("*")
    .eq("collaboration_id", collaborationId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(mapAnnotation);
}

export async function listAssignments(
  client: SupabaseClient,
  collaborationId: string,
): Promise<CoachAssignment[]> {
  const { data, error } = await client
    .from("coach_assignments")
    .select("*")
    .eq("collaboration_id", collaborationId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(mapAssignment);
}

// --- Drill templates (Library) ---

const DRILL_TEMPLATE_COLUMNS =
  "id,owner_user_id,title,detail,focus_tag,default_duration_swings,default_duration_minutes,is_archived,created_at,updated_at";

export async function listDrillTemplates(
  client: SupabaseClient,
  options: { includeArchived?: boolean } = {},
): Promise<CoachDrillTemplate[]> {
  let query = client
    .from("coach_drill_templates")
    .select(DRILL_TEMPLATE_COLUMNS)
    .order("updated_at", { ascending: false });
  if (!options.includeArchived) {
    query = query.eq("is_archived", false);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map(mapDrillTemplate);
}

export async function createDrillTemplate(
  client: SupabaseClient,
  userId: string,
  draft: CoachDrillTemplateDraft,
): Promise<CoachDrillTemplate> {
  const normalizedTitle = draft.title.trim();
  if (!normalizedTitle) throw new Error("Drill title is required.");
  const { data, error } = await client
    .from("coach_drill_templates")
    .insert({
      owner_user_id: userId,
      title: normalizedTitle,
      detail: draft.detail,
      focus_tag: draft.focusTag,
      default_duration_swings: draft.defaultDurationSwings,
      default_duration_minutes: draft.defaultDurationMinutes,
    })
    .select(DRILL_TEMPLATE_COLUMNS)
    .single();
  if (error) throw error;
  return mapDrillTemplate(data as Record<string, unknown>);
}

export async function updateDrillTemplate(
  client: SupabaseClient,
  id: string,
  patch: Partial<CoachDrillTemplateDraft> & { isArchived?: boolean },
): Promise<CoachDrillTemplate> {
  const updates: Record<string, unknown> = {};
  if (patch.title !== undefined) {
    const trimmed = patch.title.trim();
    if (!trimmed) throw new Error("Drill title cannot be empty.");
    updates.title = trimmed;
  }
  if (patch.detail !== undefined) updates.detail = patch.detail;
  if (patch.focusTag !== undefined) updates.focus_tag = patch.focusTag;
  if (patch.defaultDurationSwings !== undefined)
    updates.default_duration_swings = patch.defaultDurationSwings;
  if (patch.defaultDurationMinutes !== undefined)
    updates.default_duration_minutes = patch.defaultDurationMinutes;
  if (patch.isArchived !== undefined) updates.is_archived = patch.isArchived;

  const { data, error } = await client
    .from("coach_drill_templates")
    .update(updates)
    .eq("id", id)
    .select(DRILL_TEMPLATE_COLUMNS)
    .single();
  if (error) throw error;
  return mapDrillTemplate(data as Record<string, unknown>);
}

export async function deleteDrillTemplate(
  client: SupabaseClient,
  id: string,
): Promise<void> {
  const { error } = await client
    .from("coach_drill_templates")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

/**
 * Prescribe a drill template to a student by creating a coach_assignments
 * row with the template's id recorded as provenance. The collaboration
 * must already be accepted and the caller must be its coach.
 */
export async function assignDrillTemplate(
  client: SupabaseClient,
  params: {
    template: CoachDrillTemplate;
    collaborationId: string;
    userId: string;
    sessionId?: string | null;
    dueDate?: string | null;
    detailOverride?: string;
    sendImmediately?: boolean;
  },
): Promise<CoachAssignment> {
  const detail = params.detailOverride ?? params.template.detail;
  const { data, error } = await client
    .from("coach_assignments")
    .insert({
      collaboration_id: params.collaborationId,
      author_user_id: params.userId,
      session_id: params.sessionId ?? null,
      title: params.template.title,
      detail,
      due_date: params.dueDate ?? null,
      status: params.sendImmediately ? "sent" : "draft",
      is_cue: false,
      source_drill_template_id: params.template.id,
    })
    .select("*")
    .single();
  if (error) throw error;
  return mapAssignment(data as Record<string, unknown>);
}

export async function fetchUnauthPreview(
  client: SupabaseClient,
  previewToken: string,
): Promise<UnauthPreview | null> {
  const { data, error } = await client.rpc("coach_brief_preview", {
    p_preview_token: previewToken,
  });
  if (error) throw error;
  return data ? normalizeUnauthPreview(data as UnauthPreviewServer) : null;
}

export async function runArcCoachPlan(
  client: SupabaseClient,
  collaborationId: string,
): Promise<ArcCoachPlanResult> {
  const { data, error } = await client.functions.invoke("arc-ai-coach-plan", {
    body: { collaboration_id: collaborationId },
  });
  if (error) {
    throw new Error((await edgeFunctionErrorMessage(error)) ?? error.message);
  }
  if (!data || typeof data !== "object") {
    throw new Error("ARC Coach did not return a response.");
  }

  const result = data as ArcCoachPlanResult;
  if (!result.ok) {
    throw new Error(result.error ?? "ARC Coach could not create a plan.");
  }
  return result;
}

async function edgeFunctionErrorMessage(error: {
  context?: unknown;
}): Promise<string | null> {
  if (!(error.context instanceof Response)) return null;
  const payload = await error.context
    .clone()
    .json()
    .catch(() => null);
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  const message = (payload as Record<string, unknown>).error;
  return typeof message === "string" && message.trim() ? message : null;
}

function normalizeSupabaseClientError(error: unknown, fallback: string): Error {
  if (error instanceof Error) return error;
  if (typeof error === "string" && error.trim()) return new Error(error.trim());
  if (error && typeof error === "object") {
    const row = error as Record<string, unknown>;
    const message = typeof row.message === "string" ? row.message.trim() : "";
    if (message) return new Error(message);
    const details = typeof row.details === "string" ? row.details.trim() : "";
    if (details) return new Error(details);
  }
  return new Error(fallback);
}

export function buildArcCoachActionDraft(
  collaboration: Collaboration,
): ArcCoachActionDraft {
  const brief = collaboration.briefV2;
  const topDriver = brief?.causalDrivers?.[0] ?? null;
  const playbook = brief?.playbook ?? null;
  const focus = firstText([
    playbook?.weeklyTheme,
    topDriver?.title,
    brief?.executiveSummary?.topPriority,
    collaboration.summary,
    "One-week coach focus",
  ]);
  const objective = firstText([
    playbook?.nextSessionObjective,
    brief?.executiveSummary?.topPriority,
    "Run one focused range block before changing the cue.",
  ]);
  const cue = firstText([
    topDriver?.correctiveCue,
    playbook?.guardrail,
    brief?.executiveSummary?.topPriority,
    "Hold one cue through the full block.",
  ]);
  const verification = firstText([
    playbook?.verificationTest,
    playbook?.successCriteria,
    "Send the final-five note after the block.",
  ]);
  const blocks: CoachRangePlanBlock[] = [
    {
      id: "warmup",
      balls: 8,
      title: "Warm-up",
      club: "Assigned club",
      camera: "Coach choice",
      cadence: "Comfortable pace.",
      how: "Smooth swings at 70%. Don't correct anything yet.",
      goal: "Loosen up and find rhythm.",
      intent: "warmup",
    },
    {
      id: "baseline",
      balls: 12,
      title: "Baseline read",
      club: "Assigned club",
      camera: "Same angle",
      cadence: "7-8 seconds between balls.",
      how: "Stock swings only. No cue work.",
      goal: "Find the starting pattern before changing anything.",
      intent: "stock",
      successCriteria: "Name the dominant miss before moving on.",
    },
    {
      id: "cue-block",
      balls: 18,
      title: "Cue block",
      club: "Assigned club",
      camera: "Same angle",
      cadence: "8 seconds between balls.",
      how: cue,
      goal: "Check whether the cue changes the pattern.",
      intent: "stock",
      successCriteria: "Miss pattern shifts toward neutral on the back half.",
    },
    {
      id: "transfer-block",
      balls: 14,
      title: "Transfer block",
      club: "Assigned club + one longer club",
      camera: "Same angle",
      cadence: "Full routine.",
      how: verification,
      goal: "Check whether the pattern holds when the club changes.",
      intent: "stock",
    },
    {
      id: "pressure-finish",
      balls: 8,
      title: "Pressure finish",
      club: "Assigned club",
      camera: "Same angle",
      cadence: "Full routine, no cleanup swings.",
      how: "Score each shot as playable or not.",
      goal: "Leave with an honest final read.",
      intent: "pressure",
    },
  ];
  const prescription: CoachRangePlanPrescription = {
    id: `arc-coach-${collaboration.id}`,
    templateId: "",
    title: `ARC draft: ${focus}`,
    kind: "coach_plan",
    totalBalls: blocks.reduce((sum, block) => sum + block.balls, 0),
    summary: objective,
    setup: "Start ARC in Coach Plan mode with the assigned club and target.",
    coachCue: cue,
    homework: verification,
    betweenBlocks: "Pause between blocks, name the miss, then continue.",
    focusLabel: focus,
    clubLabel: "Assigned club",
    blocks,
  };

  return {
    mode: "plan",
    title: prescription.title,
    body: [
      objective,
      `Cue: ${cue}`,
      `Check: ${verification}`,
      "",
      ...blocks.map(
        (block) => `${block.balls} balls - ${block.title} · ${block.club} · ${block.how}`,
      ),
    ].join("\n"),
    suggestedRangePlanId: "next-lesson-homework",
    rangePlanPrescription: prescription,
  };
}

function firstText(values: Array<string | null | undefined>): string {
  for (const value of values) {
    const trimmed = typeof value === "string" ? value.trim() : "";
    if (trimmed) return trimmed;
  }
  return "";
}

function mapCollaboration(row: Record<string, unknown>): Collaboration {
  return {
    id: String(row.id),
    title: String(row.title ?? ""),
    summary: String(row.summary ?? ""),
    ownerEmail: (row.owner_email as string | null) ?? null,
    acceptedUserId: (row.accepted_user_id as string | null) ?? null,
    role: (row.role as Collaboration["role"]) ?? "coach",
    status: (row.status as Collaboration["status"]) ?? "pending",
    sessionId: (row.session_id as string | null) ?? null,
    shareCode: String(row.share_code ?? ""),
    previewToken: (row.preview_token as string | null) ?? null,
    briefV2: (row.brief_v2 as CoachBriefV2 | null) ?? null,
    latestRangePlanStatus: normalizeLatestRangePlanStatus(row.latest_range_plan_status),
    updatedAt: String(row.updated_at ?? ""),
  };
}

export function normalizeLatestRangePlanStatus(
  value: unknown,
): CoachRangePlanPortalStatus | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const schemaVersion = parseNullableNumber(row.schemaVersion);
  const totalBalls = parseNullableNumber(row.totalBalls);
  const completedBalls = parseNullableNumber(row.completedBalls);
  if (
    schemaVersion === null ||
    totalBalls === null ||
    completedBalls === null ||
    typeof row.assignmentId !== "string" ||
    typeof row.collaborationId !== "string" ||
    typeof row.planId !== "string" ||
    typeof row.planTitle !== "string" ||
    typeof row.focusLabel !== "string" ||
    typeof row.clubLabel !== "string" ||
    typeof row.updatedAt !== "string"
  ) {
    return null;
  }

  const state =
    row.state === "active" || row.state === "paused" || row.state === "completed"
      ? row.state
      : "paused";
  const blocks = Array.isArray(row.blocks)
    ? row.blocks
        .map(normalizeLatestRangePlanStatusBlock)
        .filter((block): block is CoachRangePlanPortalStatusBlock => block !== null)
    : [];

  return {
    schemaVersion,
    assignmentId: row.assignmentId,
    collaborationId: row.collaborationId,
    sessionId: typeof row.sessionId === "string" ? row.sessionId : null,
    planId: row.planId,
    planTitle: row.planTitle,
    focusLabel: row.focusLabel,
    clubLabel: row.clubLabel,
    state,
    totalBalls,
    completedBalls,
    currentBlockId: typeof row.currentBlockId === "string" ? row.currentBlockId : null,
    currentBlockTitle:
      typeof row.currentBlockTitle === "string" ? row.currentBlockTitle : null,
    nextBlockTitle: typeof row.nextBlockTitle === "string" ? row.nextBlockTitle : null,
    updatedAt: row.updatedAt,
    completedAt: typeof row.completedAt === "string" ? row.completedAt : null,
    blocks,
  };
}

function normalizeLatestRangePlanStatusBlock(
  value: unknown,
): CoachRangePlanPortalStatusBlock | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const completedBalls = parseNullableNumber(row.completedBalls);
  const totalBalls = parseNullableNumber(row.totalBalls);
  if (
    completedBalls === null ||
    totalBalls === null ||
    typeof row.id !== "string" ||
    typeof row.title !== "string" ||
    typeof row.club !== "string" ||
    typeof row.camera !== "string"
  ) {
    return null;
  }
  const state =
    row.state === "completed" || row.state === "active" || row.state === "pending"
      ? row.state
      : "pending";

  return {
    id: row.id,
    title: row.title,
    club: row.club,
    camera: row.camera,
    completedBalls,
    totalBalls,
    state,
  };
}

function mapAnnotation(row: Record<string, unknown>): CoachAnnotation {
  return {
    id: String(row.id),
    collaborationId: String(row.collaboration_id),
    authorUserId: String(row.author_user_id),
    authorKind: mapAuthorKind(row.author_kind),
    sessionId: (row.session_id as string | null) ?? null,
    swingId: (row.swing_id as string | null) ?? null,
    title: String(row.title ?? ""),
    body: String(row.body ?? ""),
    tag: (row.tag as CoachAnnotation["tag"]) ?? "note",
    scope: (row.scope as CoachAnnotation["scope"]) ?? "session",
    audioPath: (row.audio_path as string | null) ?? null,
    audioDurationSeconds: parseNullableNumber(row.audio_duration_seconds),
    createdAt: String(row.created_at ?? ""),
    updatedAt: String(row.updated_at ?? ""),
  };
}

/**
 * Parse a nullable numeric column that may arrive as a number or as a
 * string (Postgres NUMERIC columns round-trip through PostgREST as
 * strings to preserve precision).
 */
export function parseNullableNumber(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw === "string") {
    // `Number("")` is 0 in JavaScript — treat blank strings as null
    // so PostgREST never silently backfills a 0 duration on rows that
    // arrived without the column set. Pre-trim to reject "   " too.
    const trimmed = raw.trim();
    if (trimmed === "") return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function mapAssignment(row: Record<string, unknown>): CoachAssignment {
  return {
    id: String(row.id),
    collaborationId: String(row.collaboration_id),
    authorUserId: String(row.author_user_id),
    authorKind: mapAuthorKind(row.author_kind),
    sessionId: (row.session_id as string | null) ?? null,
    title: String(row.title ?? ""),
    detail: String(row.detail ?? ""),
    dueDate: (row.due_date as string | null) ?? null,
    status: (row.status as CoachAssignment["status"]) ?? "draft",
    isCue: Boolean(row.is_cue ?? false),
    sourceDrillTemplateId:
      (row.source_drill_template_id as string | null) ?? null,
    rangePlanPrescription: mapRangePlanPrescription(row.range_plan_prescription),
    createdAt: String(row.created_at ?? ""),
    updatedAt: String(row.updated_at ?? ""),
  };
}

function mapAuthorKind(value: unknown): CoachAuthorKind {
  return value === "player" || value === "coach" || value === "arc_ai"
    ? value
    : "coach";
}

function mapRangePlanPrescription(value: unknown): CoachRangePlanPrescription | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const blocks = Array.isArray(row.blocks)
    ? row.blocks.map(mapRangePlanBlock)
    : [];

  const prerequisites = Array.isArray(row.prerequisites)
    ? row.prerequisites
        .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
        .filter((entry) => entry.length > 0)
    : undefined;

  const groundTruth = mapRangePlanGroundTruth(row.groundTruth);

  return {
    id: String(row.id ?? ""),
    templateId: String(row.templateId ?? ""),
    title: String(row.title ?? ""),
    kind: (row.kind as CoachRangePlanKind) ?? "coach_plan",
    totalBalls: Number(row.totalBalls ?? 0),
    summary: String(row.summary ?? ""),
    setup: String(row.setup ?? ""),
    coachCue: String(row.coachCue ?? ""),
    homework: String(row.homework ?? ""),
    betweenBlocks: String(row.betweenBlocks ?? ""),
    focusLabel: String(row.focusLabel ?? ""),
    clubLabel: String(row.clubLabel ?? ""),
    blocks,
    ...(prerequisites && prerequisites.length > 0 ? { prerequisites } : {}),
    ...(groundTruth ? { groundTruth } : {}),
  };
}

function mapRangePlanGroundTruth(
  value: unknown,
): CoachRangePlanGroundTruth | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const rawMethod = typeof row.method === "string" ? row.method : "";
  const method = (COACH_RANGE_PLAN_GROUND_TRUTH_METHODS as readonly string[]).includes(
    rawMethod,
  )
    ? (rawMethod as CoachRangePlanGroundTruthMethod)
    : null;
  if (!method) return null;
  const cadence =
    typeof row.cadence === "string" && row.cadence.trim().length > 0
      ? row.cadence.trim()
      : null;
  return { method, cadence };
}

function mapRangePlanBlock(value: unknown): CoachRangePlanBlock {
  const row =
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const intent =
    typeof row.intent === "string" &&
    (COACH_RANGE_PLAN_BLOCK_INTENTS as readonly string[]).includes(row.intent)
      ? (row.intent as CoachRangePlanBlockIntent)
      : undefined;
  const successCriteria =
    typeof row.successCriteria === "string" && row.successCriteria.trim().length > 0
      ? row.successCriteria.trim()
      : null;
  const checkpoint =
    typeof row.checkpoint === "string" && row.checkpoint.trim().length > 0
      ? row.checkpoint.trim()
      : null;
  return {
    id: String(row.id ?? ""),
    balls: Number(row.balls ?? 0),
    title: String(row.title ?? ""),
    club: String(row.club ?? ""),
    camera: String(row.camera ?? ""),
    cadence: String(row.cadence ?? ""),
    how: String(row.how ?? ""),
    goal: String(row.goal ?? ""),
    ...(intent ? { intent } : {}),
    ...(successCriteria ? { successCriteria } : {}),
    ...(checkpoint ? { checkpoint } : {}),
  };
}

function mapDrillTemplate(row: Record<string, unknown>): CoachDrillTemplate {
  const focus = String(row.focus_tag ?? "general") as CoachDrillTemplateFocusTag;
  return {
    id: String(row.id),
    ownerUserId: String(row.owner_user_id),
    title: String(row.title ?? ""),
    detail: String(row.detail ?? ""),
    focusTag: (COACH_DRILL_TEMPLATE_FOCUS_TAGS as readonly string[]).includes(focus)
      ? focus
      : "general",
    defaultDurationSwings: (row.default_duration_swings as number | null) ?? null,
    defaultDurationMinutes: (row.default_duration_minutes as number | null) ?? null,
    isArchived: Boolean(row.is_archived ?? false),
    createdAt: String(row.created_at ?? ""),
    updatedAt: String(row.updated_at ?? ""),
  };
}
